import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Table,
    Button,
    Tag,
    Avatar,
    Card,
    Space,
    Typography,
    Tooltip,
    Badge,
    Input,
} from "antd";
import {
    PhoneOutlined,
    ReloadOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    AlertOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import SummaryApi from "../common/index";

const { Text, Title } = Typography;

const AVATAR_PALETTE = [
    "#1f6fef",
    "#1f9d57",
    "#1b2a55",
    "#f59e0b",
    "#8b5cf6",
    "#ef4444",
    "#0ea5e9",
    "#10b981",
];

const parseDateTime = (dateStr) => {
    if (!dateStr || dateStr.startsWith("0000-00-00")) return null;
    const normalized = dateStr.includes("T") ? dateStr : dateStr.replace(" ", "T");
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
};

const FollowupsTable = ({ onCall, onFollowUp }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState(""); // <-- NEW
    const hasFetchedRef = useRef(false);

    const fetchFollowups = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const userRaw = sessionStorage.getItem("user");
            if (!userRaw) {
                setItems([]);
                return;
            }

            const parsedUser = JSON.parse(userRaw);
            const memberType = parsedUser?.member_type;

            let apiConfig;
            let requestBody = null;

            if (memberType === "Employee") {
                apiConfig = SummaryApi.getAllFollowUpsByEmployee;
                requestBody = JSON.stringify({ employee_id: parsedUser.employee_id });
            } else if (memberType === "Team Lead") {
                apiConfig = SummaryApi.getAllFollowUpsByTeamLead;
                requestBody = JSON.stringify({ team_lead_emp_id: parsedUser.employee_id });
            } else if (memberType === "Admin" || memberType === "Manager") {
                apiConfig = SummaryApi.getAllFollowUps;
            } else {
                setItems([]);
                return;
            }

            const response = await fetch(apiConfig.url, {
                method: apiConfig.method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer sandeep_university_916",
                },
                body:
                    requestBody && apiConfig.method.toLowerCase() !== "get"
                        ? requestBody
                        : undefined,
            });

            const result = await response.json();

            if (!result?.status || !Array.isArray(result?.data)) {
                setItems([]);
                if (result?.message && !result.status) {
                    setError(result.message);
                }
                return;
            }

            const rawArray = result.data;
            const nowMs = Date.now();

            // Deduplicate per student_id and resolve the highest priority record
            const map = new Map();
            rawArray.forEach((r) => {
                const sid = r.student_id;
                const dateStr = r.next_follow_up_date || r.next_followup_date_time;
                const dateObj = parseDateTime(dateStr);
                if (!dateObj) return;

                const tMs = dateObj.getTime();
                const isOverdue = tMs < nowMs;
                const existing = map.get(sid);

                if (!existing) {
                    map.set(sid, { row: r, tMs, isOverdue });
                    return;
                }

                if (isOverdue && existing.isOverdue) {
                    if (tMs > existing.tMs) map.set(sid, { row: r, tMs, isOverdue });
                } else if (!isOverdue && existing.isOverdue) {
                    map.set(sid, { row: r, tMs, isOverdue });
                } else if (!isOverdue && !existing.isOverdue) {
                    if (tMs < existing.tMs) map.set(sid, { row: r, tMs, isOverdue });
                }
            });

            // Sort: upcoming earliest first, then overdue latest first
            const sorted = Array.from(map.values()).sort((a, b) => {
                if (a.isOverdue && !b.isOverdue) return 1;
                if (!a.isOverdue && b.isOverdue) return -1;
                return a.isOverdue ? b.tMs - a.tMs : a.tMs - b.tMs;
            });

            const formatted = sorted.map((entry, index) => {
                const item = entry.row;
                const rawName = (item.student_name || "Unknown Student").trim();
                const initials =
                    rawName
                        .split(" ")
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((w) => w[0]?.toUpperCase())
                        .join("") || "?";

                const followupDate = parseDateTime(
                    item.next_follow_up_date || item.next_followup_date_time
                );
                const isValid = Boolean(followupDate);
                const isOverdue = isValid && followupDate.getTime() < nowMs;

                let dateLabel = "—";
                let timeLabel = "";
                if (isValid) {
                    const day = String(followupDate.getDate()).padStart(2, "0");
                    const month = followupDate.toLocaleString("en-US", { month: "short" });
                    const year = followupDate.getFullYear();
                    dateLabel = `${day} ${month} ${year}`;

                    let hours = followupDate.getHours();
                    const minutes = String(followupDate.getMinutes()).padStart(2, "0");
                    const ampm = hours >= 12 ? "PM" : "AM";
                    hours = hours % 12 || 12;
                    timeLabel = `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
                }

                let subText = null;
                if (isValid) {
                    const diffMs = followupDate.getTime() - nowMs;
                    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
                    if (isOverdue) {
                        subText = "Overdue — follow up now";
                    } else if (diffDays === 0) {
                        const diffHours = Math.round(diffMs / (1000 * 60 * 60));
                        subText =
                            diffHours <= 0
                                ? "Due very soon"
                                : `${diffHours} hour${diffHours === 1 ? "" : "s"} from now`;
                    } else if (diffDays === 1) {
                        subText = "Tomorrow";
                    } else if (diffDays > 1) {
                        subText = `${diffDays} days from now`;
                    }
                }

                return {
                    key: item.student_id ? String(item.student_id) : `fu-${index}`,
                    sno: index + 1,
                    studentId: item.student_id,
                    name: rawName,
                    initials,
                    avatarBg: AVATAR_PALETTE[index % AVATAR_PALETTE.length],
                    date: dateLabel,
                    time: timeLabel,
                    isOverdue,
                    subText,
                    phone: item.mobile_number1 || item.mobile || "N/A",
                    remarks: item.lead_description || item.remarks || "—",
                    raw: item,
                    followupdate: item?.follow_up_date,
                    employee: item?.employee_name
                };
            });

            setItems(formatted);
        } catch (err) {
            console.error("[FollowupsTable] Fetch failed:", err);
            setError("Failed to load follow-ups");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        fetchFollowups();
    }, [fetchFollowups]);

    const handleCall = (record) => {
        if (onCall) {
            onCall(record);
        } else if (record.phone && record.phone !== "N/A") {
            window.location.href = `tel:${record.phone}`;
        }
    };

    // Filter across ALL rows so search works regardless of active page
    const filteredItems = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return items;
        return items.filter((item) => {
            const nameMatch = (item.name || "").toLowerCase().includes(q);
            const phoneMatch = String(item.phone || "")
                .toLowerCase()
                .includes(q);
            return nameMatch || phoneMatch;
        });
    }, [items, searchTerm]);

    const columns = useMemo(
        () => [
            {
                title: "S.No",
                dataIndex: "sno",
                key: "sno",
                width: 65,
                align: "center",
            },
            {
                title: "Employee",
                dataIndex: "employee",
                key: "employee",
                render: (_, record) => (
                    <Space orientation="horizontal" size="middle">
                        <div>
                            <Text strong style={{ color: "#14213d", display: "block" }}>
                                {record.employee}
                            </Text>

                        </div>
                    </Space>
                ),
            },

            {
                title: "Student",
                dataIndex: "name",
                key: "name",
                render: (_, record) => (
                    <Space orientation="horizontal" size="middle">
                        <Avatar
                            style={{
                                backgroundColor: record.avatarBg,
                                verticalAlign: "middle",
                                fontWeight: 600,
                            }}
                        >
                            {record.initials}
                        </Avatar>
                        <div>
                            <Text strong style={{ color: "#14213d", display: "block" }}>
                                {record.name}
                            </Text>

                        </div>
                    </Space>
                ),
            },
            {
                title: "Mobile Number",
                dataIndex: "phone",
                key: "phone",
                render: (phone) => (
                    <Text style={{ fontFamily: "monospace", fontSize: 13 }}>
                        {phone}
                    </Text>
                ),
            },
            {
                title: "Last Interaction",
                dataIndex: "followupdate",
                key: "followupdate",
                render: (_, record) => (
                    <Space orientation="horizontal" size="middle">
                        <div>
                            <Text strong style={{ color: "#14213d", display: "block" }}>
                                {record.followupdate
                                    && new Date(record.followupdate).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                    }) === "Invalid Date" ? "Not Specified"
                                    : new Date(record.followupdate).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                    })}
                            </Text>
                        </div>
                    </Space>
                ),
            },
            {
                title: "Scheduled Follow-up",
                key: "followup",
                render: (_, record) => (
                    <Space direction="vertical" size={2}>
                        <Space size={6}>
                            <Tag
                                color={record.isOverdue ? "error" : "processing"}
                                icon={
                                    record.isOverdue ? (
                                        <ClockCircleOutlined />
                                    ) : (
                                        <CalendarOutlined />
                                    )
                                }
                                style={{ borderRadius: 6, margin: 0 }}
                            >
                                {record.date} {record.time && `• ${record.time}`}
                            </Tag>
                            {record.isOverdue && (
                                <Tooltip title="Requires immediate attention">
                                    <Badge count={<AlertOutlined style={{ color: "#ef4444" }} />} />
                                </Tooltip>
                            )}
                        </Space>
                        {record.subText && (
                            <Text
                                type={record.isOverdue ? "danger" : "secondary"}
                                style={{ fontSize: 12, fontWeight: record.isOverdue ? 600 : 400 }}
                            >
                                {record.subText}
                            </Text>
                        )}
                    </Space>
                ),
            },
            {
                title: "Remarks",
                dataIndex: "remarks",
                key: "remarks",
                ellipsis: true,
                render: (remarks) => (
                    <Tooltip title={remarks} placement="topLeft">
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {remarks}
                        </Text>
                    </Tooltip>
                ),
            },
            {
                title: "Actions",
                key: "actions",
                width: 140,
                align: "center",
                render: (_, record) => (
                    <Space orientation="horizontal" size="small">
                        <Button
                            type="default"
                            icon={<PhoneOutlined />}
                            onClick={() => handleCall(record)}
                            disabled={!record.phone || record.phone === "N/A"}
                            size="small"
                            style={{
                                borderColor: "#d9e2ec",
                                borderRadius: 6,
                            }}
                        >
                            Call
                        </Button>
                        {onFollowUp && (
                            <Button
                                type="primary"
                                size="small"
                                danger={record.isOverdue}
                                onClick={() => onFollowUp(record.raw)}
                                style={{ borderRadius: 6 }}
                            >
                                Update
                            </Button>
                        )}
                    </Space>
                ),
            },
        ],
        [onFollowUp]
    );

    const overdueCount = useMemo(
        () => items.filter((i) => i.isOverdue).length,
        [items]
    );

    return (
        <Card
            title={
                <Space align="center" size="middle">
                    <Title level={5} style={{ margin: 0, color: "#14213d" }}>
                        Follow-up Reminders
                    </Title>
                    <Badge
                        count={
                            overdueCount > 0
                                ? `${overdueCount} Overdue`
                                : `${items.length} Pending`
                        }
                        style={{
                            backgroundColor: overdueCount > 0 ? "#ef4444" : "#1f6fef",
                        }}
                    />
                </Space>
            }
            extra={
                <Space size="small">
                    <Input
                        allowClear
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search name or mobile..."
                        prefix={<SearchOutlined style={{ color: "#8c8c8c" }} />}
                        style={{ width: 240 }}
                    />
                    <Button
                        type="text"
                        icon={<ReloadOutlined spin={loading} />}
                        onClick={fetchFollowups}
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Space>
            }
            style={{
                borderRadius: 12,
                border: "1px solid #e8ecf4",
                boxShadow: "0 2px 8px rgba(20, 33, 61, 0.04)",
            }}
            bodyStyle={{ padding: 0 }}
        >
            {error && (
                <div style={{ padding: "12px 16px", background: "#fff2f0" }}>
                    <Text type="danger">{error}</Text>
                </div>
            )}
            <Table
                columns={columns}
                dataSource={filteredItems}
                loading={loading}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ["5", "10", "20", "50"],
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} students`,
                }}
                size="middle"
                locale={{
                    emptyText: searchTerm
                        ? "No students match your search."
                        : "No follow-ups scheduled. You're all caught up.",
                }}
                rowClassName={(record) => (record.isOverdue ? "ant-table-row-overdue" : "")}
            />
        </Card>
    );
};

export default FollowupsTable;